import { FormikValues } from "formik";
import {
  CustomFrequencyUnit,
  EndType,
  RecurringFrequency,
} from "../../../../../components/app/provider/scheduling/RecurringConfig";
import { AsyncSelectValue } from "../../../../../components/AsyncSelect";
import { createMapper } from "../../../../../utils/utils";

export const INTAKE_FORM_SECTIONS = [
  // { value: "demographics", label: "Demographics" }, // we added patient information form for the new demographics form.
  { value: "clientInformationForm", label: "Client Information Form" },
  { value: "clientHistoryForm", label: "Client History Form" },
  { value: "clientInsuranceForm", label: "Client Insurance Form" },
  // { value: "insurance", label: "Insurance" },
  { value: "paymentPolicy", label: "Payment Policy" },
  // { value: "patientMedicalHistory", label: "Patient Medical History" },
  // { value: "HIPAA Consent", label: "HIPAA Consent" }, // commented: replaced by "Consent For Services"
  { value: "consentForServices", label: "Consent For Services" },
  {
    value: "aiConsent",
    label: "Consent For Use Of Artificial Intelligence",
  },
  { value: "privacyPractices", label: "Notice Of Privacy Practices" },
  { value: "releaseOfInformation", label: "Release Of Information" },
  { value: "medicalServicesAgreement", label: "Medical Services Agreement" },
  {
    value: "physicianPatientArbitrationAgreement",
    label: "Physician Patient Arbitration Agreement",
  },
  {
    value: "Individual Psychotherapy",
    label: "Individual Psychotherapy",
  },
  {
    value: "Psychoeducation Therapy",
    label: "Psychoeducation Therapy",
  },
];

export const INTAKE_FORM_SECTIONS_MAPPER = createMapper(INTAKE_FORM_SECTIONS);

// Maps each intake/consent form name to its Form Type label
export const FORM_TYPE_MAPPER: Record<string, "Intake" | "Consent"> = {
  // Demographics: "Intake",
  // Insurance: "Intake",
  "Payment Policy": "Intake",
  // "Patient Medical History": "Intake",
  "Individual Psychotherapy": "Intake",
  "Psychoeducation Therapy": "Intake",
  // "HIPAA Consent": "Consent",
  "Consent For Services": "Consent",
  "Consent For Use Of Artificial Intelligence": "Consent",
  "Notice Of Privacy Practices": "Consent",
  "Release Of Information": "Consent",
  "Medical Services Agreement": "Consent",
  "Physician Patient Arbitration Agreement": "Consent",
  "Client Information Form": "Intake",
  "Client History Form": "Intake",
  "Client Insurance Form": "Intake",
};

export interface NewAppointmentType extends FormikValues {
  patientId: { value: string; label: string; birthDate: string } | undefined;
  visit_type: string;
  assessments?: string;
  customForms?: string[];
  paymentType: "CASH" | "INSURANCE";
  type: string;
  locationId?: { value: string; label: string };
  timezone?: any;
  providerSpecialities?: any;
  providerId: AsyncSelectValue | undefined;
  startTime: string;
  endTime: Date;
  insurance_type: string;
  note: string;
  primary_insurance?: { uuid: string };
  secondary_insurance?: { uuid: string };
  authorization: string;
  networkStatus?: string;
  // Independent network status for the secondary insurance. Primary keeps
  // using `networkStatus`; the secondary insurance is verified separately.
  secondaryNetworkStatus?: string;
  copayAmount?: string;
  coInsuranceAmount?: string;
  deductible?: string;
  coverageLevel?: string;
  priorAuthorization?: boolean;
  verificationStatus?: string;
  forms: string[];
  sendForms: boolean;

  isRecurring: boolean;
  recurringFrequency: RecurringFrequency;
  reminder_set: boolean;

  endType: EndType;
  endDate: Date;
  endAfter: number;

  customFrequency: number;
  customFrequencyUnit: CustomFrequencyUnit;
  selectedWeekdays: number[];
  reminders?: {
    reminderBeforeNumber: number;
    reminderBeforeUnit: string;
    reminderMedium: string[];
  };
}

export const NEW_APPOINTMENT_INITIAL_VALUES: NewAppointmentType = {
  mode: "IN_PERSON",
  patientId: undefined,
  customForms: undefined,
  visit_type: "",
  sendForms: false,
  type: "",
  paymentType: "INSURANCE",
  locationId: undefined,
  assessments: undefined,
  providerSpecialities: undefined,
  providerId: undefined,
  startTime: new Date() as any,
  endTime: new Date(),
  insurance_type: "",
  note: "",
  primary_insurance: undefined,
  secondary_insurance: undefined,
  authorization: "",
  networkStatus: undefined,
  secondaryNetworkStatus: undefined,
  copayAmount: undefined,
  coInsuranceAmount: undefined,
  deductible: undefined,
  coverageLevel: undefined,
  priorAuthorization: undefined,
  verificationStatus: undefined,
  forms: [],
  chiefComplaint: "",
  timezone: "",

  isRecurring: false,
  recurringFrequency: "daily",
  reminder_set: false,

  endType: "on_date",
  endDate: new Date(),
  endAfter: 5,

  customFrequency: 1,
  customFrequencyUnit: "days",
  selectedWeekdays: [],
  reminders: {
    reminderBeforeNumber: 1,
    reminderBeforeUnit: "DAYS",
    reminderMedium: [],
  },
};

export const NETWORK_STATUS = {
  IN_NETWORK: "IN_NETWORK",
  OUT_OF_NETWORK: "OUT_OF_NETWORK",
} as const;

export const NETWORK_STATUS_OPTIONS = [
  { value: NETWORK_STATUS.IN_NETWORK, label: "In Network" },
  { value: NETWORK_STATUS.OUT_OF_NETWORK, label: "Out of Network" },
];

// Coverage fields captured against the primary insurance. They are cleared
// together whenever the primary insurance or its network status changes so a
// previous payer's values are never carried over.
export const PRIMARY_COVERAGE_FIELDS = [
  "copayAmount",
  "coInsuranceAmount",
  "deductible",
  "coverageLevel",
  "priorAuthorization",
  "verificationStatus",
] as const;

export const REMINDER_MEDIUMS = [
  { value: "EMAIL", label: "E-Mail" },
  { value: "SMS", label: "SMS" },
  { value: "PUSH_NOTIFICATION", label: "Push Notification" },
];

export const REMINDER_BEFORE_UNITS = [
  { value: "MINUTES", label: "Minutes" },
  { value: "HOURS", label: "Hours" },
  { value: "DAYS", label: "Days" },
];

/* Individual/Group toggle at the top of the Schedule Appointment drawer. */
export const SCHEDULE_SCOPE_TOGGLE_SX = {
  border: "none",
  borderBottom: "none",
  backgroundColor: "transparent",
  padding: 0,
  mb: 2,
};
